import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTheme } from '../components/ThemeContext';
import StockService from '../Api_Module/Api_Services/StockService';

interface Holding {
  symbol: string;
  name: string;
  currentShares: number;
  previousShares: number;
  currentWeight: number;
  previousWeight: number;
  currentValue: number;
  previousValue: number;
  currency: string;
  payoffProfile: string;
  shareChangeAbsolute: number;
  shareChangePct: number;
  weightChangePct: number;
  valueChangePct: number;
  status: string;
  formattedCurrentShares: string;
  formattedPreviousShares: string;
  formattedShareChange: string;
  formattedShareChangePct: string;
  formattedCurrentWeight: string;
  formattedPreviousWeight: string;
  formattedWeightChangePct: string;
  formattedCurrentValue: string;
  formattedPreviousValue: string;
  formattedValueChangePct: string;
  formattedPortfolioWeight: string;
  approximatePrice: number;
  formattedApproximatePrice: string;
  investorInsight: string;
}

interface Etf {
  symbol: string;
  name: string;
  price: number;
  change: number;
  changePercent: number;
}

interface ETFDetailProps {
  selectedEtf?: Etf | null;
  holdings?: Holding[];
}

const COLORS = [
  '#3B82F6', '#FB923C', '#8B5CF6', '#10B981', '#EF4444',
  '#06B6D4', '#F97316', '#84CC16', '#EC4899', '#6366F1',
  '#14B8A6', '#F43F5E', '#8B5CF6', '#EAB308', '#22C55E',
  '#A855F7', '#FB923C', '#4ADE80', '#F472B6', '#818CF8'
];

const ETFDetail: React.FC<ETFDetailProps> = ({ selectedEtf: propEtf, holdings: propHoldings }) => {
  const { symbol } = useParams<{ symbol: string }>();
  const { theme } = useTheme();

  const [etfData, setEtfData] = useState<Etf | null>(propEtf || null);
  const [holdingsData, setHoldingsData] = useState<Holding[]>(propHoldings || []);
  const [loading, setLoading] = useState(!propEtf && !propHoldings);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // If props are provided, use them and don't fetch
    if (propEtf || propHoldings) {
      if (propEtf) setEtfData(propEtf);
      if (propHoldings) setHoldingsData(propHoldings);
      setLoading(false);
      return;
    }

    // Otherwise, fetch data based on symbol
    const fetchData = async () => {
      if (!symbol) return;

      setLoading(true);
      try {
        // Fetch ETF holdings (Institutional Ownership History is the closest proxy available in StockService for now)
        // Adjust endpoint as needed based on actual API capabilities for ETFs
        const res = await StockService.getInstitutionalOwnershipHistory(symbol);

        // Mocking transformation or usage since specific ETF endpoint wasn't clear in service
        // Assuming we get some data structure we can map to Holding[]
        // For now, if no specific ETF endpoint exists, we might show a placeholder or basic info
        // This is a placeholder implementation:
        if (res && res.holdings) {
          setHoldingsData(res.holdings);
        } else {
          // setError('ETF data not available via public API');
        }
      } catch (err: any) {
        console.error('Failed to fetch ETF details', err);
        setError('Failed to load ETF data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [symbol, propEtf, propHoldings]);

  if (loading) {
    return <div className="p-8 text-center">Loading ETF Data...</div>;
  }

  // Fallback if no data found
  if (!propEtf && !etfData && (!holdingsData || holdingsData.length === 0)) {
    // If we are just viewing the page layout without real data linkage yet:
    // We can just return the message or mocked data if preferred.
    // For this fix, let's allow it to render empty state cleanly.
  }

  const currentHoldings = holdingsData;
  // If no data, show message
  if (currentHoldings.length === 0) {
    return (
      <div className={`p-8 rounded-lg text-center ${theme === 'dark' ? 'bg-theme-secondary' : 'bg-white'}`}>
        <p>No holdings data available for {symbol || 'this ETF'}.</p>
      </div>
    );
  }

  // Prepare data for pie chart (Top 15 + Others)
  const top15Holdings = currentHoldings.slice(0, 15);
  const othersWeight = currentHoldings.slice(15).reduce((sum, h) => sum + h.currentWeight, 0);

  const pieData = [
    ...top15Holdings.map(h => ({
      name: h.symbol,
      value: h.currentWeight,
      formattedValue: h.formattedPortfolioWeight
    })),
    ...(othersWeight > 0 ? [{
      name: 'Other',
      value: othersWeight,
      formattedValue: `${othersWeight.toFixed(2)}%`
    }] : [])
  ];

  return (
    <div className="space-y-6">
      {error && (
        <div className={`p-8 rounded-lg text-center ${theme === 'dark' ? 'bg-theme-secondary text-red-400' : 'bg-white text-red-600'}`}>
          <p>Error: {error}</p>
        </div>
      )}

      {!error && currentHoldings.length > 0 && (
        <>
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold text-theme-primary">{symbol?.toUpperCase()} Holdings</h1>
          </div>

          {/* Four Containers in One Row */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Portfolio Allocation */}
            <div className={`p-4 md:p-6 rounded-lg ${theme === 'dark' ? 'bg-theme-secondary' : 'bg-white'}`}>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Portfolio Allocation</h3>
              </div>
              <div className="space-y-2">
                {pieData.map((item, index) => (
                  <div key={item.name} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      />
                      <span className="text-sm">{item.name}</span>
                    </div>
                    <span className="text-sm font-medium">{item.formattedValue}</span>
                  </div>
                ))}
              </div>
              <div className="text-center mt-4 text-xs text-theme-muted">
                Powered by StockFortress
              </div>
            </div>

            {/* Top Buys */}
            <div className={`p-4 md:p-6 rounded-lg ${theme === 'dark' ? 'bg-theme-secondary' : 'bg-white'}`}>
              <h3 className="text-lg font-semibold mb-4">Top Buys</h3>
              <div className="space-y-3">
                {top15Holdings.slice(0, 5).map((holding, index) => (
                  <div key={holding.symbol} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className={`text-sm ${theme === 'dark' ? 'text-theme-muted' : 'text-gray-600'}`}>{index + 1}</span>
                      <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center">
                        <span className="text-theme-primary text-xs">🔒</span>
                      </div>
                      <div className="opacity-50 blur-sm">
                        <div className="text-sm font-medium">•••••••</div>
                        <div className="text-xs text-gray-500">••••</div>
                      </div>
                    </div>
                    <span className="text-sm font-semibold text-green-500">
                      +${(parseFloat(holding.formattedCurrentValue.replace(/[$M]/g, '')) / 10).toFixed(2)}B
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Top Sells */}
            <div className={`p-4 md:p-6 rounded-lg ${theme === 'dark' ? 'bg-theme-secondary' : 'bg-white'}`}>
              <h3 className="text-lg font-semibold mb-4">Top Sells</h3>
              <div className="space-y-3">
                {top15Holdings.slice(0, 5).map((holding, index) => (
                  <div key={holding.symbol + '-sell'} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className={`text-sm ${theme === 'dark' ? 'text-theme-muted' : 'text-gray-600'}`}>{index + 1}</span>
                      <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center">
                        <span className="text-theme-primary text-xs">🔒</span>
                      </div>
                      <div className="opacity-50 blur-sm">
                        <div className="text-sm font-medium">•••••••</div>
                        <div className="text-xs text-gray-500">••••</div>
                      </div>
                    </div>
                    <span className="text-sm font-semibold text-red-500">
                      +${(parseFloat(holding.formattedCurrentValue.replace(/[$M]/g, '')) / 15).toFixed(2)}B
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Sector Composition */}
            <div className={`p-4 md:p-6 rounded-lg ${theme === 'dark' ? 'bg-theme-secondary' : 'bg-white'}`}>
              <h3 className="text-lg font-semibold mb-4">Sector Composition</h3>
              <div className="space-y-2">
                {[
                  { name: 'Technology', percentage: '31.18%', color: '#3B82F6' },
                  { name: 'Financials', percentage: '13.97%', color: '#10B981' },
                  { name: 'Consumer Discretionary', percentage: '10.14%', color: '#FB923C' },
                  { name: 'Healthcare', percentage: '9.38%', color: '#EF4444' },
                  { name: 'Industrials', percentage: '9.06%', color: '#8B5CF6' }
                ].map((sector, index) => (
                  <div key={sector.name} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={`text-sm ${theme === 'dark' ? 'text-theme-muted' : 'text-gray-600'}`}>{index + 1}</span>
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: sector.color }}
                      />
                      <span className="text-sm">{sector.name}</span>
                    </div>
                    <span className="text-sm font-medium">{sector.percentage}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Top 15 Holdings Table */}
          <div className={`p-4 md:p-6 rounded-lg ${theme === 'dark' ? 'bg-theme-secondary' : 'bg-white'}`}>
            <h2 className="text-xl font-semibold mb-4">
              Top 15 Holdings out of {currentHoldings.length}
            </h2>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className={`border-b ${theme === 'dark' ? 'border-theme-tertiary' : 'border-gray-200'}`}>
                    <th className="text-left py-3 px-2">Ticker</th>
                    <th className="text-left py-3 px-2">Company</th>
                    <th className="text-right py-3 px-2">% Portfolio</th>
                    <th className="text-right py-3 px-2">Market Value</th>
                    <th className="text-right py-3 px-2">Shares</th>
                    <th className="text-right py-3 px-2">Chg. Shares %</th>
                    <th className="text-right py-3 px-2">Chg. Shares</th>
                    <th className="text-right py-3 px-2">% Owned</th>
                  </tr>
                </thead>
                <tbody>
                  {top15Holdings.map((holding, index) => (
                    <tr
                      key={holding.symbol}
                      className={`border-b ${theme === 'dark' ? 'border-theme-tertiary hover:bg-gray-750' : 'border-gray-100 hover:bg-gray-50'}`}
                    >
                      <td className="py-3 px-2 font-medium">
                        <div className="flex items-center gap-2">
                          <div className={`w-6 h-6 rounded flex items-center justify-center text-xs ${theme === 'dark' ? 'bg-theme-tertiary' : 'bg-gray-200'}`}>
                            {holding.symbol.charAt(0)}
                          </div>
                          {holding.symbol}
                        </div>
                      </td>
                      <td className="py-3 px-2">{holding.name}</td>
                      <td className="py-3 px-2 text-right">{holding.formattedPortfolioWeight}</td>
                      <td className="py-3 px-2 text-right">{holding.formattedCurrentValue}</td>
                      <td className="py-3 px-2 text-right">{holding.formattedCurrentShares}</td>
                      <td className={`py-3 px-2 text-right ${holding.shareChangePct > 0 ? 'text-green-500' :
                        holding.shareChangePct < 0 ? 'text-red-500' : ''
                        }`}>
                        {holding.formattedShareChangePct}
                      </td>
                      <td className="py-3 px-2 text-right">{holding.formattedShareChange}</td>
                      <td className="py-3 px-2 text-right">
                        {holding.formattedCurrentWeight}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ETFDetail;
